import {
  PrivilegeType as DeprecatedPrivilegeType,
  KineticModule,
  PrivilegeType,
} from '@kinetic-ui/data-layer';
import { EntityTypes } from 'const';
import { UserContext } from 'contexts/UserContext/UserContext';
import { useContext } from 'react';

// Check if user has privileges for a specific entityId
export const useEntityPrivilege = (
  privilegeType: PrivilegeType | DeprecatedPrivilegeType,
  module: KineticModule | string,
  entityType: EntityTypes,
  entityId: string
) => {
  const { privileges: userPrivileges } = useContext(UserContext);
  const privilege = userPrivileges?.[module]?.[privilegeType]?.[0];

  if (!privilege) {
    return false;
  }

  // If restrictedEntities is not defined or empty, user has access to all entities
  if (!privilege.restrictedEntities || Object.keys(privilege.restrictedEntities).length === 0) {
    return true;
  }

  return privilege.restrictedEntities[entityType]?.includes(entityId);
};

/** Check if module contains all passed in PrivilegeType */
export const useAllPrivileges = (
  requiredPrivileges: (PrivilegeType | DeprecatedPrivilegeType)[],
  module: KineticModule | string
) => {
  const { privileges: userPrivileges } = useContext(UserContext);
  return !userPrivileges?.[module]
    ? false
    : requiredPrivileges.every((permission) => userPrivileges[module][permission]);
};

/** Check if module has any permissions */
export const useHasPrivileges = (module?: KineticModule | string) => {
  const { privileges: userPrivileges } = useContext(UserContext);

  if (!module || !userPrivileges) {
    return true;
  }

  return !userPrivileges[module] ? false : Object.keys(userPrivileges[module]).length > 0;
};

/** Check if module has any of the passed in PrivilegeType */
export const useAnyPrivilege = (
  privileges: (PrivilegeType | DeprecatedPrivilegeType)[],
  module: KineticModule | string
) => {
  const { privileges: userPrivileges } = useContext(UserContext);

  if (!privileges || !userPrivileges) {
    return true;
  }

  return privileges.some(
    (privilege) => userPrivileges[module] && Object.keys(userPrivileges[module]).includes(privilege)
  );
};

/** Check if module has ONLY of the passed in PrivilegeType for the passed module.
 *
 * If the user has NO privileges for the module, returns false.
 *
 * Otherwise, returns true if the user has no privileges for the module other than the privileges passed in.
 */
export const useOnlyPrivileges = (
  privileges: (PrivilegeType | DeprecatedPrivilegeType)[],
  module: KineticModule | string
) => {
  const { privileges: userPrivileges } = useContext(UserContext);
  const hasModulePrivileges = useHasPrivileges(module);

  if (!hasModulePrivileges) {
    return false;
  }
  const modulePrivileges = Object.keys(userPrivileges[module]) as (
    | PrivilegeType
    | DeprecatedPrivilegeType
  )[];

  return modulePrivileges.every((userPrivilege) => privileges.includes(userPrivilege));
};
export type PrivilegeSet = {
  privileges: (PrivilegeType | DeprecatedPrivilegeType)[];
  module: KineticModule | string;
}[];

/** Check if user has all privileges for multiple modules */
export const useAllMultipleModulePrivileges = (privilegeSet: PrivilegeSet | undefined) => {
  const { privileges: userPrivileges } = useContext(UserContext);
  if (!privilegeSet) {
    return true;
  }
  const isAllowed = privilegeSet.every(({ privileges, module }) => {
    return !userPrivileges?.[module]
      ? false
      : privileges.every((permission) => userPrivileges[module][permission]);
  });

  if (!isAllowed) {
    console.debug('useAllMultipleModulePrivileges denied for privilegeSet:', privilegeSet);
  }
  return isAllowed;
};

/** Check if user has all privileges for any privilege in a privilege set, supporting multiple modules
 *
 * the user needs to have all privileges in each privilege object but we'll return true if any of the privilege objects are satisfied
 */
export const useAnyMultipleModulePrivileges = (privilegeSet: PrivilegeSet | undefined) => {
  const { privileges: userPrivileges } = useContext(UserContext);
  if (!privilegeSet || !privilegeSet.length) {
    return true;
  }
  return privilegeSet.some(({ privileges, module }) => {
    return !userPrivileges?.[module]
      ? false
      : privileges.every((permission) => userPrivileges[module][permission]);
  });
};
